import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { multimediaContactCenterAgentEnabledSelector, multimediaContactCenterSupervisorEnabledSelector, selectedSiteSelector } from '../../authentication/redux'
import React, { useCallback, useMemo, useState } from 'react'
import { AmccAgent, ServerPage, Supervisor } from '../../app/types'
import { Sort, useInfiniteScroll } from '../../common/components/table/useInfiniteScroll'
import { api, buildPaginatedUrl } from '../../common/api'
import { ColumnDefinition, InfiniteScrollTable } from '../../common/components/table/Table'
import { Grid, Link, Typography } from '@mui/material'
import TableRowActionsMenu from '../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../common/components/table/SorterHeader'
import { SearchField } from '../../common/components/SearchField'
import ErrorAlert from '../../common/components/ErrorAlert'
import DeletionModal from '../../common/components/DeletionModal'
import { RouteDefinition } from '../../app/App'
import { TitledBody } from '../../common/components/TitledBody'
import { AddSupervisor } from './addSupervisor'
import { AddButton } from '../../common/components/AddButton'
import { DetailSupervisor } from './detail'
import { usePrivilege } from '../../common/util/hooks'
import { FeaturePrivileges } from '../../common/util/featurePrivileges'

import { AddAgents } from './AddAgents'

export const detailSupervisorUrl = '/services/contact-center/supervisor/:id'
export const addSupervisorUrl = '/services/contact-center/supervisor/new'

export const routes: RouteDefinition[] = [
    {
        href: addSupervisorUrl,
        component: AddSupervisor,
        id: 'amcc-supervisor-add',
        parentId: 'contact-center-overview',
        translationKey: 'supervisors.add.title',
    },
    {
        href: detailSupervisorUrl,
        component: DetailSupervisor,
        id: 'amcc-supervisor-detail',
        parentId: 'contact-center-overview',
    }
].map(routeDefinition => ({ ...routeDefinition, menuId: 'contact-center-overview' }))

export const AmccSupervisorList = () => {
    const history = useHistory()
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const supervisorsEnabled= useSelector(multimediaContactCenterSupervisorEnabledSelector);
    const [addAgents, setAddAgents] = useState<boolean>(false)
    const agentsEnabled= useSelector(multimediaContactCenterAgentEnabledSelector);
    const [supervisorToDelete, setSupervisorToDelete] = useState<Supervisor | null>(null)
    const [agentToDelete, setAgentToDelete] = useState<AmccAgent | null>(null)
    const { hasAccess: hasWriteAccess, onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.callCenters.write)

    const fetchSupervisors = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<Supervisor>> => {
            if (!site) return { content: [], totalPages: 0, number: 0, totalElements: 0 }
            const { data } = await api.get(buildPaginatedUrl(`/api/site/${site.productId}/amcc/supervisors`, pageParam, sortParam, searchTermParam))
            return data
        },
        [site]
    )

    const fetchAgents = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<AmccAgent>> => {
            if (!site) return { content: [], totalPages: 0, number: 0, totalElements: 0 }
            const { data } = await api.get(buildPaginatedUrl(`/api/site/${site.productId}/amcc/agents`, pageParam, sortParam, searchTermParam))
            return data
        },
        [site]
    )

    const { sort: sortSupervisors, setSort: setSortSupervisors, setSearchTerm: setSupervisorSearchTerm, rows: supervisorRows, error: supervisorError, fetchNextPage: fetchSupervisorNextPage, hasNextPage: hasSupervisorNextPage, isFetching: isSupervisorFetching, refetch: refetchSupervisors } = useInfiniteScroll<Supervisor>({
        queryKey: `supervisors-${site?.productId}`,
        initialSort: { field: 'lastName', order: 'asc' },
        fetchFunction: fetchSupervisors,
    })

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<AmccAgent>({
        queryKey: `agents-${site?.productId}`,
        initialSort: { field: 'lastName', order: 'asc' },
        fetchFunction: fetchAgents,
    })

    const supervisorRowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: (record: Supervisor) => {
                    history.push(detailSupervisorUrl.replace(':id', record.id || '') + '?edit=true#0')
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: Supervisor) => {
                    setSupervisorToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, history]
    )

    const agentRowActions = useMemo(
        () => [
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: AmccAgent) => {
                    setAgentToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t]
    )

    const supervisorColumns: ColumnDefinition<Supervisor>[] = useMemo(
        () => [
            {
                title: t('call-pickup-groups.table.name'),
                key: 'userLastName',
                sorter: true,
                render: (text: string, record: Supervisor) => <Link href={detailSupervisorUrl.replace(':id', record.id || '')}>{`${record.userLastName} ${record.userFirstName}`}</Link>,
            },
            {
                title: t('supervisors.supervised-sites'),
                key: 'userFirstName',
                sorter: false,
                render: (text: string, record: Supervisor) =>
                    <>
                        {record.superviseAllSites ? t('supervisors.all-sites') :
                            (record.supervisedSites == null || record.supervisedSites.length === 0) ?
                                t('general.none') : record.supervisedSites.map(ss => ss.siteName).join(', ')}
                    </>,
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (text: string, record: Supervisor) => <TableRowActionsMenu record={record} menuItems={supervisorRowActions} readOnly={!hasWriteAccess} />,
                style: { width: '25%' },
            },
        ],
        [t, supervisorRowActions, hasWriteAccess]
    )

    const agentColumns: ColumnDefinition<AmccAgent>[] = useMemo(
        () => [
            {
                title: t('call-pickup-groups.table.name'),
                key: 'userLastName',
                sorter: true,
                render: (text: string, record: AmccAgent) =>  <>{`${record.userLastName} ${record.userFirstName}`}</>,
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (text: string, record: AmccAgent) => <TableRowActionsMenu record={record} menuItems={agentRowActions} readOnly={!hasWriteAccess} />,
                style: { width: '25%' },
            },
        ],
        [t, agentRowActions, hasWriteAccess]
    )

    const onSupervisorSearch = useCallback(async (searchTerm: string) => setSupervisorSearchTerm(searchTerm), [setSupervisorSearchTerm])

    const onAgentSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])

    const supervisorSorter = useCallback(col => sorterHeader(col, sortSupervisors, setSortSupervisors), [sortSupervisors, setSortSupervisors])

    const agentSorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getSupervisorRowKey = useCallback(supervisor => supervisor.uuid, [])

    const getAgentRowKey = useCallback(agent => agent.uuid, [])

    const getDeleteUrl = useCallback((e: Supervisor | null) => (site && e ? `/api/site/${site.productId}/amcc/supervisors/${e.id}` : null), [site])

    const getAgentDeleteUrl = useCallback((e: AmccAgent | null) => (site && e ? `/api/site/${site.productId}/amcc/agents/${e.id}` : null), [site])

    return (
        <TitledBody title={t('navigation.multimedia-contact-center')}>
            <Grid container spacing={1} width={1}>
                {supervisorsEnabled &&
                    <Grid item justifyContent={'space-between'} flexDirection={'row'} width={1} mb={3}>
                        <Typography component='div' variant='h6' color='secondary' gutterBottom>
                            {t(`contact-centers.supervisors.title`)}
                        </Typography>
                        <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={1} flexWrap={'nowrap'}>
                            <Grid item xs={'auto'} pr={1}>
                                <SearchField onSearch={onSupervisorSearch}/>
                            </Grid>
                            <Grid item>
                                {
                                    onlyWithWriteAccess(<AddButton
                                        onClick={() => {
                                            history.push(addSupervisorUrl)
                                            return null
                                        }}
                                        label={`${t('supervisors.create-new')}`}
                                    />)
                                }
                            </Grid>
                        </Grid>
                        <ErrorAlert showAlert={!!supervisorError} />
                        <InfiniteScrollTable<Supervisor>
                            hasNextPage={hasSupervisorNextPage}
                            data={supervisorRows}
                            fetchNextPage={fetchSupervisorNextPage}
                            isFetching={isSupervisorFetching}
                            getRowKey={getSupervisorRowKey}
                            sorter={supervisorSorter}
                            columns={supervisorColumns}
                            tableId={'amccSupervisorsTable'}
                        />
                        <DeletionModal<Supervisor | null>
                            nameToDelete={supervisorToDelete => supervisorToDelete?.userLastName + ' ' + supervisorToDelete?.userFirstName}
                            itemToDelete={supervisorToDelete} deleteUrl={getDeleteUrl} onClose={() => setSupervisorToDelete(null)} onSuccess={refetchSupervisors} />
                    </Grid>
                }
                {agentsEnabled &&
                    <Grid item justifyContent={'space-between'} flexDirection={'row'} width={1} mb={3}>
                        <Typography component='div' variant='h6' color='secondary' gutterBottom>
                            {t(`contact-centers.agents.title`)}
                        </Typography>
                        <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={1} flexWrap={'nowrap'}>
                            <Grid item xs={'auto'} pr={1}>
                                <SearchField onSearch={onAgentSearch}/>
                            </Grid>
                            <Grid item>
                                {
                                    onlyWithWriteAccess(<AddButton
                                        onClick={() => {
                                            setAddAgents(true)
                                            return null
                                        }}
                                        label={`${t('contact-centers.agents.add')}`}
                                    />)
                                }
                            </Grid>
                        </Grid>
                        <ErrorAlert showAlert={!!error} />
                        <InfiniteScrollTable<AmccAgent>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={isFetching}
                            getRowKey={getAgentRowKey}
                            sorter={agentSorter}
                            columns={agentColumns}
                            tableId={'amccAgentsTable'}
                        />
                        <DeletionModal<AmccAgent | null>
                            nameToDelete={agentToDelete => agentToDelete?.userLastName + ' ' + agentToDelete?.userFirstName}
                            itemToDelete={agentToDelete} deleteUrl={getAgentDeleteUrl} onClose={() => setAgentToDelete(null)} onSuccess={refetch} />
                        {addAgents && <AddAgents onClose={() => {setAddAgents(false); refetch();}}/>}
                    </Grid>
                }
            </Grid>
        </TitledBody>
    )
}